import React from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  computeRootCauseTriggers,
  I3Zone,
  RootCauseTriggerResult,
} from '../../models/ImmuneInflammatory';
import {
  ROOT_CAUSE_GUIDANCE,
  ROOT_CAUSE_GUIDANCE_TABS,
  RootCauseGuidance,
  RootCauseGuidanceTab,
} from '../../models/rootCauseGuidance';

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
}

// Zone → priority chip wording + colors (kept in step with the score panel).
const PRIORITY_META: Record<I3Zone, { label: string; fill: string; fg: string }> = {
  green: { label: 'Low priority', fill: '#78F880', fg: '#14521d' },
  yellow: { label: 'Moderate priority', fill: '#FEFA83', fg: '#2e2a00' },
  orange: { label: 'Elevated priority', fill: '#FFD37E', fg: '#3d2700' },
  red: { label: 'High priority', fill: '#FC6552', fg: '#fff' },
};

export class RootCauseGuidanceTabs extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis): boolean {
    return !!ba && !!ba.bloodwork;
  }

  renderChip(zone: I3Zone, score: number) {
    const meta = PRIORITY_META[zone];
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '1px 8px',
          borderRadius: '10px',
          background: meta.fill,
          color: meta.fg,
          fontSize: '11px',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        {meta.label} · {Math.round(score)}%
      </span>
    );
  }

  renderGuidanceBlock(
    g: RootCauseGuidance,
    tabKey: RootCauseGuidanceTab,
    trigger: RootCauseTriggerResult | undefined,
  ) {
    const bullets = g[tabKey];
    return (
      <div className="condition-section" key={g.key} style={{ textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ color: 'black', fontWeight: 700, fontSize: '14px' }}>{g.label}</span>
          {trigger && this.renderChip(trigger.zone, trigger.score)}
        </div>
        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{g.subtitle}</div>
        <ul style={{ fontSize: '12.5px', margin: '6px 0 0', paddingLeft: '18px' }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ marginBottom: '2px' }}>
              {b}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis || !bloodworkAnalysis.bloodwork) return null;

    const triggers = computeRootCauseTriggers(bloodworkAnalysis.bloodwork);
    const byKey = new Map<string, RootCauseTriggerResult>(triggers.map(t => [t.key, t]));
    // Order the four areas by their computed score so the most-relevant guidance
    // is on top within each tab, consistent with the trigger bars in the panel.
    const ordered = [...ROOT_CAUSE_GUIDANCE].sort(
      (a, b) => (byKey.get(b.key)?.score ?? 0) - (byKey.get(a.key)?.score ?? 0),
    );

    return (
      <div id="root-cause-guidance-tabs">
        <Tabs
          defaultActiveKey="assessment"
          id="root-cause-guidance-tabs-nav"
          transition={false}
          mountOnEnter
          unmountOnExit
        >
          {ROOT_CAUSE_GUIDANCE_TABS.map(tab => (
            <Tab eventKey={tab.key} title={tab.label} key={tab.key}>
              <br />
              {ordered.map(g => this.renderGuidanceBlock(g, tab.key, byKey.get(g.key)))}
            </Tab>
          ))}
        </Tabs>
        <div style={{ textAlign: 'left', fontSize: '11px', color: '#888', marginTop: '8px' }}>
          Clinical guidance from the I³ Root Cause Framework (Rountree/Strand). For
          practitioner use — assess all four triggers for every client.
        </div>
      </div>
    );
  }
}

export default RootCauseGuidanceTabs;
